// Template models for document request templates

export interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  items: DocumentTemplateItem[];
}

export interface DocumentTemplateItem {
  id: string;
  nameKey: string;
  customName: string;
  descriptionKey: string;
  customDescription: string;
  isRequired: boolean;
  templateFile?: DocumentTemplateFile | null;
  order: number;
}

export interface DocumentTemplateFile {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: string;
  fileData: string; // Base64 encoded file data
  uploadDate: string;
}

export interface TemplateAssignment {
  id: string;
  templateId: string;
  customerId: number;
  caseId: string;
  assignedAt: string;
  assignedBy: string;
}

// Helper function to generate a unique ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
};

// Helper function to create a new empty template
export const createEmptyTemplate = (userId: string): DocumentTemplate => {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    name: '',
    description: '',
    createdAt: now,
    updatedAt: now,
    createdBy: userId,
    items: []
  };
};

// Helper function to create a new template item
export const createTemplateItem = (isRequired: boolean = false): DocumentTemplateItem => {
  return {
    id: generateId(),
    nameKey: '',
    customName: '',
    descriptionKey: '',
    customDescription: '',
    isRequired,
    templateFile: null,
    order: 0
  };
};

// Save templates to localStorage
export const saveTemplates = (templates: DocumentTemplate[]): void => {
  localStorage.setItem('documentTemplates', JSON.stringify(templates));
};

// Load templates from localStorage
export const loadTemplates = (): DocumentTemplate[] => {
  const saved = localStorage.getItem('documentTemplates');
  return saved ? JSON.parse(saved) : [];
};

// Save template assignments to localStorage
export const saveTemplateAssignments = (assignments: TemplateAssignment[]): void => {
  localStorage.setItem('templateAssignments', JSON.stringify(assignments));
};

// Load template assignments from localStorage
export const loadTemplateAssignments = (): TemplateAssignment[] => {
  const saved = localStorage.getItem('templateAssignments');
  return saved ? JSON.parse(saved) : [];
};

// Get the template assignment for a specific customer
export const getCustomerTemplateAssignment = (customerId: number): TemplateAssignment | undefined => {
  const assignments = loadTemplateAssignments();
  return assignments.find(a => a.customerId === customerId);
};

// Get the template for a specific customer
export const getCustomerTemplate = (customerId: number): DocumentTemplate | undefined => {
  const assignment = getCustomerTemplateAssignment(customerId);
  if (!assignment) return undefined;
  
  const templates = loadTemplates();
  return templates.find(t => t.id === assignment.templateId);
};
